import {
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth';
import { auth, googleProvider, supportGoogleProvider } from './config';
import { isAnyClientPortalHost } from './portalHosts';
import { isStandaloneDisplay, markGoogleRedirectPending } from '../utils/pwaDisplay';

function pageOrigin() {
  try {
    return window.location.origin;
  } catch (_) {
    return '';
  }
}

function buildStaffGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  provider.setCustomParameters({ hd: 'plexarian.com', prompt: 'select_account' });
  return provider;
}

/**
 * Portal cliente: popup Firebase (mismo handler autorizado que Nexus).
 * Evita GIS / origin_mismatch del Client ID web en localhost.
 */
export async function signInWithPortalGoogle() {
  if (!isAnyClientPortalHost()) {
    throw Object.assign(
      new Error('Este método solo aplica al portal de cliente.'),
      { code: 'auth/operation-not-supported-in-this-environment' }
    );
  }

  try {
    await signInWithPopup(auth, supportGoogleProvider);
    return { method: 'firebase-popup-portal' };
  } catch (err) {
    if (auth.currentUser) {
      return { method: 'firebase-popup-portal', alreadySignedIn: true };
    }
    throw err;
  }
}

/** @deprecated Usar signInWithPortalGoogle — se mantiene por compatibilidad de imports. */
export async function mountPortalGoogleSignInButton() {
  throw Object.assign(
    new Error('El portal usa el botón Acceder con Google (Firebase popup).'),
    { code: 'auth/operation-not-supported-in-this-environment' }
  );
}

export function mapGoogleWorkspaceAuthError(code, message) {
  const origin = pageOrigin();
  if (
    code === 'auth/origin-mismatch'
    || /origin_mismatch/i.test(message || '')
    || /The given origin is not allowed/i.test(message || '')
    || /FedCM/i.test(message || '')
  ) {
    return (
      `Google rechazó el origen de esta página (${origin || 'desconocido'}). `
      + 'Usa exactamente http://localhost:3000?portal=licencias y recarga.'
    );
  }
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Inicio de sesión cancelado.';
    case 'auth/popup-blocked':
      if (isLocalHost()) {
        return 'Este navegador no puede abrir la ventana de Google. Entra desde Chrome en http://localhost:3000.';
      }
      return isStandaloneDisplay()
        ? 'La app instalada no puede abrir la ventana de Google. Cierra y vuelve a entrar; si sigue, ábrela una vez en Chrome.'
        : 'El navegador bloqueó la ventana de Google. Permite popups e intenta de nuevo.';
    case 'auth/network-request-failed':
      return 'Error de red al contactar a Google. Revisa tu conexión.';
    case 'auth/account-exists-with-different-credential':
      return 'Conflicto de sesión con Google. Recarga e intenta de nuevo.';
    case 'auth/unauthorized-domain':
      return (
        `Este dominio no está autorizado en Firebase Auth (${origin || 'desconocido'}). `
        + 'En local usa http://localhost:3000.'
      );
    case 'auth/operation-not-supported-in-this-environment':
      return message || 'Operación no disponible en este entorno.';
    default:
      if (message && !String(message).includes('Firebase:')) return message;
      return 'No se pudo iniciar sesión con Google. Intenta de nuevo.';
  }
}

function isLocalHost() {
  try {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  } catch (_) {
    return false;
  }
}

async function signInStaffWithRedirect(provider) {
  markGoogleRedirectPending();
  await signInWithRedirect(auth, provider, browserPopupRedirectResolver);
  return { method: 'firebase-redirect' };
}

async function signInStaffWithProvider(provider) {
  // PWA instalada no puede abrir popup.
  // En localhost no hay fallback a redirect: Chrome parte el storage del
  // dominio de auth (nexus.plexarian.com), Google muestra la cuenta y
  // getRedirectResult vuelve vacío.
  if (isStandaloneDisplay()) {
    return signInStaffWithRedirect(provider);
  }
  try {
    await signInWithPopup(auth, provider);
    return { method: 'firebase-popup' };
  } catch (err) {
    if (auth.currentUser) {
      return { method: 'firebase-popup', alreadySignedIn: true };
    }
    if (err?.code === 'auth/popup-blocked' && !isLocalHost()) {
      return signInStaffWithRedirect(provider);
    }
    throw err;
  }
}

/**
 * Staff Nexus: popup en el mismo click (sin awaits previos: Chrome pierde el gesto
 * y el popup queda en blanco o nunca abre). Redirect solo en PWA instalada.
 */
export async function signInWithGoogleWorkspace() {
  if (isAnyClientPortalHost()) {
    return signInWithPortalGoogle();
  }

  return signInStaffWithProvider(googleProvider || buildStaffGoogleProvider());
}

export function getPageOriginForDebug() {
  return pageOrigin();
}
